import { useEffect, useMemo, useRef, useState } from "react";
import { BsCheck, BsChevronDown, BsSearch } from "react-icons/bs";
import { searchByProp } from "utils/search";

function SearchableSelect({
  className = "",
  contentClassName = "",
  options = [],
  value,
  onChange,
  onValueChange,
  onSelect,
  placeholder = "Select",
  searchPlaceholder = "Search",
  emptyText = "No options found",
  renderOption,
  renderValue,
  searchFields = ["label", "name", "searchText"],
  disabled = false,
  showCheck = true,
  height = 30,
  ...triggerProps
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const containerRef = useRef(null);
  const searchInputRef = useRef(null);

  const normalizedOptions = useMemo(
    () =>
      options.map((option) => {
        if (typeof option === "string" || typeof option === "number") {
          return {
            raw: option,
            value: option,
            label: String(option),
            searchText: String(option),
          };
        }

        const label = option.label ?? option.name ?? option.value ?? "";
        return {
          ...option,
          raw: option,
          value: option.value ?? option.id ?? label,
          label: String(label),
        };
      }),
    [options],
  );

  const filteredOptions = useMemo(
    () =>
      normalizedOptions.filter((option) =>
        searchByProp(option, searchFields, search),
      ),
    [normalizedOptions, search, searchFields],
  );

  const selectedOption = normalizedOptions.find((option) =>
    isSameValue(option.value, value),
  );
  const triggerStyle =
    height === "auto"
      ? undefined
      : { height: typeof height === "number" ? `${height}px` : height };

  useEffect(() => {
    const handleMouseDown = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        closeDropdown();
      }
    };

    document.addEventListener("mousedown", handleMouseDown);
    return () => document.removeEventListener("mousedown", handleMouseDown);
  }, []);

  useEffect(() => {
    if (!isOpen) return;

    setActiveIndex(0);
    setTimeout(() => searchInputRef.current?.focus(), 0);
  }, [isOpen]);

  useEffect(() => {
    setActiveIndex(0);
  }, [search]);

  const closeDropdown = () => {
    setIsOpen(false);
    setSearch("");
  };

  const handleSelect = (option) => {
    onChange?.(option.value);
    onValueChange?.(option.value);
    onSelect?.(option.raw);
    closeDropdown();
  };

  const handleTriggerKeyDown = (event) => {
    if (["Enter", " ", "ArrowDown"].includes(event.key)) {
      event.preventDefault();
      if (!disabled) setIsOpen(true);
    }
  };

  const handleSearchKeyDown = (event) => {
    if (event.key === "Escape") {
      event.preventDefault();
      closeDropdown();
      return;
    }

    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActiveIndex((index) =>
        Math.min(index + 1, Math.max(filteredOptions.length - 1, 0)),
      );
      return;
    }

    if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveIndex((index) => Math.max(index - 1, 0));
      return;
    }

    if (event.key === "Enter" && filteredOptions[activeIndex]) {
      event.preventDefault();
      handleSelect(filteredOptions[activeIndex]);
    }
  };

  return (
    <div
      ref={containerRef}
      className={`${className} ${isOpen ? "z-50" : "z-0"} relative`}
    >
      <button
        type="button"
        role="combobox"
        aria-expanded={isOpen}
        disabled={disabled}
        onClick={() => !disabled && setIsOpen((open) => !open)}
        onKeyDown={handleTriggerKeyDown}
        style={triggerStyle}
        className={`w-full flex items-center justify-between px-2 rounded-lg border border-gray-200 bg-white text-gray-700 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-200 focus:border-sky-600 disabled:opacity-80 disabled:cursor-not-allowed cursor-pointer min-w-0
          ${height === "auto" ? "py-1 min-h-[30px]" : ""}
          `}
        {...triggerProps}
      >
        <span
          className={`flex-1 text-left
              ${selectedOption ? "" : "text-gray-400 dark:text-gray-500"}
              ${height !== "auto" && "truncate"}
              `}
        >
          {selectedOption
            ? renderValue
              ? renderValue(selectedOption.raw)
              : selectedOption.label
            : placeholder}
        </span>
        <BsChevronDown
          className={`flex-shrink-0 ml-2 transition-transform ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </button>

      {isOpen && (
        <div
          className={`${contentClassName} absolute z-50 w-full mt-1 overflow-hidden bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg`}
        >
          <div className="relative p-2 border-b border-gray-100 dark:border-gray-700">
            <BsSearch
              className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500"
              size={14}
            />
            <input
              ref={searchInputRef}
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              onKeyDown={handleSearchKeyDown}
              placeholder={searchPlaceholder}
              className="w-full h-8 pl-8 pr-2 rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 dark:text-gray-200 placeholder-gray-300 dark:placeholder-gray-600 outline-none focus:border-sky-600 dark:focus:border-sky-700"
            />
          </div>

          <div role="listbox" className="max-h-64 overflow-auto py-1">
            {filteredOptions.length === 0 ? (
              <div className="px-3 py-6 text-center text-sm text-gray-400 dark:text-gray-500">
                {emptyText}
              </div>
            ) : (
              filteredOptions.map((option, index) => {
                const selected = isSameValue(option.value, value);
                const active = index === activeIndex;

                return (
                  <button
                    key={`${option.value}-${index}`}
                    type="button"
                    role="option"
                    aria-selected={selected}
                    onClick={() => handleSelect(option)}
                    onMouseEnter={() => setActiveIndex(index)}
                    className={`w-full flex items-center gap-2 px-3 py-2 text-left text-sm break-words transition-colors duration-150 ${
                      selected
                        ? "bg-sky-100 dark:bg-sky-900 text-sky-700 dark:text-sky-200"
                        : active
                          ? "bg-gray-100 dark:bg-gray-700"
                          : "hover:bg-gray-100 dark:hover:bg-gray-700"
                    }`}
                  >
                    <span className="min-w-0 flex-1">
                      {renderOption ? renderOption(option.raw) : option.label}
                    </span>
                    {showCheck && (
                      <BsCheck
                        className={`flex-shrink-0 text-sky-600 dark:text-sky-300 ${
                          selected ? "opacity-100" : "opacity-0"
                        }`}
                        size={18}
                      />
                    )}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function isSameValue(a, b) {
  return String(a) === String(b);
}

export default SearchableSelect;
